/**
 * Lobby layout (B4, ADR-062) — the editor for the server-driven lobby config.
 *
 * One audited setting (`lobby`) drives what the client lobby SHOWS: ordered,
 * titled rails of game slugs, and per-game badges. Presentation only — a game
 * absent from every rail is still served and still playable; a slug the
 * catalogue does not serve is dropped by the server at read time. The editor
 * edits the STORED value and flags unknown slugs so a typo is heard now rather
 * than silently shipped.
 *
 * The whole config is saved in ONE `PUT /internal/admin/settings/lobby` —
 * admin-gated, zod-validated server-side, one audit row per save.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { fetchGames } from '../api/client.ts';
import type { AdminGame } from '../api/types.ts';
import {
  fetchLobbyConfig,
  saveLobbyConfig,
  LOBBY_BADGES,
  type LobbyBadge,
  type LobbyRail,
} from '../api/lobby.ts';
import { useAsync } from '../lib/useAsync.ts';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { Badge, Card, EmptyState, ErrorState, Loading } from '../ui/primitives.tsx';
import { IconDie, IconSliders } from '../ui/icons.tsx';

const RAIL_ID_RE = /^[a-z0-9][a-z0-9_-]*$/;

interface Draft {
  rails: LobbyRail[];
  badges: Record<string, LobbyBadge>;
}

/** A stable machine id proposed from a title: "Live rounds" → "live-rounds". */
function idFromTitle(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function Lobby(): ReactNode {
  const games = useAsync(() => fetchGames(), []);
  const stored = useAsync(() => fetchLobbyConfig(), []);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  // Seed the draft once from the stored value; after that the operator's
  // edits are the truth until they save or reload.
  useEffect(() => {
    if (!stored.loading && stored.error === null && draft === null) {
      setDraft({
        rails: (stored.data?.rails ?? []).map((r) => ({ ...r, slugs: [...r.slugs] })),
        badges: { ...(stored.data?.badges ?? {}) },
      });
    }
  }, [stored.loading, stored.error, stored.data, draft]);

  if (stored.error) {
    return <ErrorState title="Could not load the lobby config" detail={stored.error} onRetry={() => { setDraft(null); stored.reload(); }} />;
  }
  if (games.error) {
    return <ErrorState title="Could not load the games list" detail={games.error} onRetry={games.reload} />;
  }
  if (stored.loading || games.loading || !draft || !games.data) {
    return <Loading>Loading the lobby config…</Loading>;
  }

  const catalogue = games.data;
  const known = new Set(catalogue.map((g) => g.slug));
  const nameOf = (slug: string): string => catalogue.find((g) => g.slug === slug)?.name ?? slug;

  const idProblems = draft.rails
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => !RAIL_ID_RE.test(r.id) || r.title.trim() === '');
  const dupIds = new Set(
    draft.rails.map((r) => r.id).filter((id, i, all) => all.indexOf(id) !== i),
  );
  const unknownSlugs = [
    ...new Set([
      ...draft.rails.flatMap((r) => r.slugs.filter((s) => !known.has(s))),
      ...Object.keys(draft.badges).filter((s) => !known.has(s)),
    ]),
  ];
  const valid = idProblems.length === 0 && dupIds.size === 0;

  function mutate(fn: (d: Draft) => Draft): void {
    setDraft((d) => (d ? fn(d) : d));
  }

  async function onSave(): Promise<void> {
    if (!draft || !valid || busy) return;
    const result = await confirm({
      title: 'Publish the lobby layout',
      body:
        'The client lobby follows this config on its next load. Unknown slugs are dropped by the server when it serves the catalogue — they are stored but never shown.',
      confirmLabel: 'Publish',
      facts: [
        { label: 'Rails', value: String(draft.rails.length) },
        { label: 'Badges', value: String(Object.keys(draft.badges).length) },
        ...(unknownSlugs.length > 0
          ? [{ label: 'Unknown slugs (dropped at read)', value: unknownSlugs.join(', ') }]
          : []),
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const saved = await saveLobbyConfig({ rails: draft.rails, badges: draft.badges });
      setDraft({ rails: saved.rails ?? [], badges: saved.badges ?? {} });
      toast('Lobby layout published.');
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the lobby config.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card
        title="Rails"
        icon={<IconSliders />}
        sub="Ordered, titled groups of games the client lobby renders top to bottom"
      >
        {draft.rails.length === 0 ? (
          <EmptyState
            compact
            icon={<IconSliders size="lg" />}
            title="No rails configured"
            hint="Without rails the client falls back to its built-in lobby. Add a rail to take over the layout."
          />
        ) : (
          draft.rails.map((rail, index) => (
            <RailEditor
              key={index}
              rail={rail}
              index={index}
              count={draft.rails.length}
              catalogue={catalogue}
              known={known}
              nameOf={nameOf}
              duplicateId={dupIds.has(rail.id)}
              onChange={(next) =>
                mutate((d) => ({ ...d, rails: d.rails.map((r, i) => (i === index ? next : r)) }))
              }
              onMove={(dir) =>
                mutate((d) => {
                  const rails = [...d.rails];
                  const to = index + dir;
                  if (to < 0 || to >= rails.length) return d;
                  const [it] = rails.splice(index, 1);
                  rails.splice(to, 0, it!);
                  return { ...d, rails };
                })
              }
              onRemove={() =>
                mutate((d) => ({ ...d, rails: d.rails.filter((_, i) => i !== index) }))
              }
            />
          ))
        )}
        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() =>
              mutate((d) => ({
                ...d,
                rails: [...d.rails, { id: `rail-${d.rails.length + 1}`, title: 'New rail', slugs: [] }],
              }))
            }
          >
            Add rail
          </button>
        </div>
      </Card>

      <Card
        title="Badges"
        icon={<IconDie />}
        sub="A small truthful label a tile may wear — new, hot, live or top"
      >
        {Object.keys(draft.badges).length === 0 ? (
          <p className="field__hint">No badges assigned.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Game</th>
                <th>Badge</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {Object.entries(draft.badges).map(([slug, badge]) => (
                <tr key={slug}>
                  <td>
                    {nameOf(slug)}{' '}
                    {!known.has(slug) ? <Badge tone="pending">unknown — dropped at read</Badge> : null}
                  </td>
                  <td>
                    <select
                      className="input input--sm"
                      value={badge}
                      onChange={(e) =>
                        mutate((d) => ({
                          ...d,
                          badges: { ...d.badges, [slug]: e.target.value as LobbyBadge },
                        }))
                      }
                    >
                      {LOBBY_BADGES.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={() =>
                        mutate((d) => {
                          const badges = { ...d.badges };
                          delete badges[slug];
                          return { ...d, badges };
                        })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <AddBadge
          catalogue={catalogue}
          assigned={new Set(Object.keys(draft.badges))}
          onAdd={(slug, badge) =>
            mutate((d) => ({ ...d, badges: { ...d.badges, [slug]: badge } }))
          }
        />
      </Card>

      <div className="row-actions row-actions--start">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !valid}
          onClick={() => void onSave()}
        >
          {busy ? 'Publishing…' : 'Publish lobby layout'}
        </button>
        {!valid ? (
          <span className="field__error">
            {dupIds.size > 0
              ? `Duplicate rail id: ${[...dupIds].join(', ')}`
              : 'Every rail needs a title and a machine id (lowercase letters, digits, - or _).'}
          </span>
        ) : unknownSlugs.length > 0 ? (
          <span className="toolbar__note">
            {unknownSlugs.length} unknown slug{unknownSlugs.length === 1 ? '' : 's'} will be dropped
            when served.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function RailEditor({
  rail,
  index,
  count,
  catalogue,
  known,
  nameOf,
  duplicateId,
  onChange,
  onMove,
  onRemove,
}: {
  rail: LobbyRail;
  index: number;
  count: number;
  catalogue: AdminGame[];
  known: Set<string>;
  nameOf: (slug: string) => string;
  duplicateId: boolean;
  onChange: (next: LobbyRail) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}): ReactNode {
  const idInvalid = !RAIL_ID_RE.test(rail.id);
  const addable = catalogue.filter((g) => !rail.slugs.includes(g.slug));

  return (
    <div className="subcard">
      <div className="row-actions row-actions--start">
        <button type="button" className="btn btn--sm btn--ghost" disabled={index === 0} onClick={() => onMove(-1)} title="Move rail up">
          ↑
        </button>
        <button type="button" className="btn btn--sm btn--ghost" disabled={index === count - 1} onClick={() => onMove(1)} title="Move rail down">
          ↓
        </button>
        <input
          className="input input--sm"
          value={rail.title}
          placeholder="Rail title"
          onChange={(e) => {
            const title = e.target.value;
            // While the id still tracks the old title, keep proposing one.
            const tracking = rail.id === idFromTitle(rail.title) || rail.id.startsWith('rail-');
            onChange({ ...rail, title, id: tracking ? idFromTitle(title) || rail.id : rail.id });
          }}
        />
        <input
          className={`input input--sm mono-code${idInvalid || duplicateId ? ' input--error' : ''}`}
          value={rail.id}
          placeholder="machine-id"
          title="Stable machine id the client keys this rail by"
          onChange={(e) => onChange({ ...rail, id: e.target.value })}
        />
        <button type="button" className="btn btn--sm btn--ghost" onClick={onRemove}>
          Remove rail
        </button>
      </div>
      {duplicateId ? <span className="field__error">This id is used by another rail.</span> : null}

      <div className="chip-row">
        {rail.slugs.length === 0 ? (
          <span className="field__hint">Empty — an empty rail is dropped when served.</span>
        ) : (
          rail.slugs.map((slug, si) => (
            <span key={slug} className="chip">
              {nameOf(slug)}
              {!known.has(slug) ? <Badge tone="pending">unknown</Badge> : null}
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                disabled={si === 0}
                title="Move left"
                onClick={() => {
                  const slugs = [...rail.slugs];
                  [slugs[si - 1], slugs[si]] = [slugs[si]!, slugs[si - 1]!];
                  onChange({ ...rail, slugs });
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                disabled={si === rail.slugs.length - 1}
                title="Move right"
                onClick={() => {
                  const slugs = [...rail.slugs];
                  [slugs[si], slugs[si + 1]] = [slugs[si + 1]!, slugs[si]!];
                  onChange({ ...rail, slugs });
                }}
              >
                ›
              </button>
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                title="Remove from rail"
                onClick={() => onChange({ ...rail, slugs: rail.slugs.filter((s) => s !== slug) })}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="row-actions row-actions--start">
        <select
          className="input input--sm"
          value=""
          onChange={(e) => {
            if (e.target.value !== '') onChange({ ...rail, slugs: [...rail.slugs, e.target.value] });
          }}
        >
          <option value="">Add a game…</option>
          {addable.map((g) => (
            <option key={g.slug} value={g.slug}>
              {g.name}
              {g.enabled ? '' : ' (disabled)'}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function AddBadge({
  catalogue,
  assigned,
  onAdd,
}: {
  catalogue: AdminGame[];
  assigned: Set<string>;
  onAdd: (slug: string, badge: LobbyBadge) => void;
}): ReactNode {
  const [slug, setSlug] = useState('');
  const [badge, setBadge] = useState<LobbyBadge>('new');
  const addable = catalogue.filter((g) => !assigned.has(g.slug));

  return (
    <div className="row-actions row-actions--start">
      <select className="input input--sm" value={slug} onChange={(e) => setSlug(e.target.value)}>
        <option value="">Pick a game…</option>
        {addable.map((g) => (
          <option key={g.slug} value={g.slug}>
            {g.name}
          </option>
        ))}
      </select>
      <select
        className="input input--sm"
        value={badge}
        onChange={(e) => setBadge(e.target.value as LobbyBadge)}
      >
        {LOBBY_BADGES.map((b) => (
          <option key={b} value={b}>
            {b}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn--sm btn--ghost"
        disabled={slug === ''}
        onClick={() => {
          onAdd(slug, badge);
          setSlug('');
        }}
      >
        Add badge
      </button>
    </div>
  );
}
